/**
 * The taskDojo mark, drawn rather than loaded.
 *
 * A direct port of taskdojo-app/src/components/DojoMark.js, sharing its
 * geometry verbatim -- the same 824-unit space lifted from assets/logo-dojo.svg,
 * so the drawn mark, the app icon and the phone cannot drift apart.
 *
 * The web was using a PNG for this, which is why the rank badge read as a
 * smudge: a raster mark cannot be tinted, so the badge lost the one thing it is
 * for. Drawn, it does what the phone's does.
 *
 * Two modes, because the mark does two different jobs:
 *
 *   two-tone    the real mark -- black face, red mask band, white eye slits.
 *               Used where it stands for the app, like the sign-in screen. The
 *               default.
 *   monochrome  the same silhouette flattened to one colour, for places where
 *               the mark is a token rather than a logo: the rank badge tints it
 *               to the current belt, and two colours inside a small ring would
 *               just be mud. Passing `color` selects this.
 *
 * The negative space is a real mask rather than shapes painted in a background
 * colour, so either mode drops onto any surface, light or dark.
 */
import React, { useId } from 'react';

/** Face and mask colours, matching assets/logo-dojo.svg. */
export const DOJO_FACE = '#0B1013';
export const DOJO_MASK = '#E34234';
export const DOJO_EYE = '#FFFFFF';

// Band and eye geometry, kept in step with assets/logo-dojo.svg.
const CX = 412;
const CY = 401.75;
const BAND_SCALE = 1.1;
const EYE_SCALE = 1.18;
// Cropped to the head plus a margin, so the mark does not sit hard against the
// edge of whatever contains it. Without the padding the eye slits ran almost
// into the rank badge's ring.
const PAD = 54;
const VIEW = `${156 - PAD} ${156 - PAD} ${512 + PAD * 2} ${512 + PAD * 2}`;
const BOX = 824;

const SQUIRCLE =
  'M 156 288 A 132 132 0 0 1 288 156 L 536 156 A 132 132 0 0 1 668 288 ' +
  'L 668 536 A 132 132 0 0 1 536 668 L 288 668 A 132 132 0 0 1 156 536 Z';
const BAND =
  'M 115 304.5 Q 284 325 412 360.8 Q 540 325 709 304.5 ' +
  'L 709 499 Q 540 478.6 412 442.7 Q 284 478.6 115 499 Z';
const BRIDGE = 'M 391.5 350.6 L 432.5 350.6 L 432.5 463.2 L 391.5 463.2 Z';
const EYE_L =
  'M 207.2 360.8 L 365.9 391.5 L 365.9 437.6 L 222.6 447.8 Q 197 412 207.2 360.8 Z';
const EYE_R =
  'M 616.8 360.8 L 458.1 391.5 L 458.1 437.6 L 601.4 447.8 Q 641 412 616.8 360.8 Z';

interface DojoMarkProps {
  size?: number;
  /** Set this to draw the flat, single-colour token instead of the logo. */
  color?: string;
  faceColor?: string;
  maskColor?: string;
  eyeColor?: string;
  /**
   * A keyline around the head, for dark grounds.
   *
   * The face is #0B1013 and so is the app's dark background, so on that ground
   * the head disappears and the mark collapses to two red wings floating in
   * space -- recognisable as a mask, but not as a logo. The icon never has this
   * problem because it is always composited onto whatever is behind it. A
   * hairline restores the silhouette without repainting the mark.
   */
  outlineColor?: string;
  className?: string;
  title?: string;
}

const DojoMark: React.FC<DojoMarkProps> = ({
  size = 40,
  color,
  faceColor = DOJO_FACE,
  maskColor = DOJO_MASK,
  eyeColor = DOJO_EYE,
  outlineColor,
  className,
  title,
}) => {
  // Mask ids must be unique per instance or two marks on one page share the
  // first one's mask.
  const n = useId().replace(/:/g, '');
  const maskId = `dojo-mask-${n}`;
  const clipId = `dojo-clip-${n}`;
  const cutId = `dojo-cut-${n}`;

  // A single `color` means "draw me as a token": both layers take that value,
  // which collapses the two-tone mark back to a flat silhouette.
  const mono = !!color;
  const face = mono ? color : faceColor;
  const ring = mono ? color : maskColor;

  const bandTransform = `translate(${CX},${CY}) scale(${BAND_SCALE}) translate(${-CX},${-CY})`;
  const eyeTransform = `translate(${CX},${CY}) scale(${EYE_SCALE}) translate(${-CX},${-CY})`;

  return (
    <svg
      width={size}
      height={size}
      viewBox={VIEW}
      className={className}
      role={title ? 'img' : 'presentation'}
      aria-label={title}
      aria-hidden={title ? undefined : true}
    >
      {title && <title>{title}</title>}
      <defs>
        <clipPath id={clipId}>
          <path d={SQUIRCLE} />
        </clipPath>

        {/* The band, with the bridge and eye slits cut back out of it. Band and
            cuts are scaled about the band's own centre to match
            assets/logo-dojo.svg; changing one without the other drifts the
            drawn mark away from the icon. */}
        <mask id={maskId} maskUnits="userSpaceOnUse" x="0" y="0" width={BOX} height={BOX}>
          <rect width={BOX} height={BOX} fill="#000" />
          <g transform={bandTransform}>
            <path d={BAND} fill="#fff" />
          </g>
          <g transform={eyeTransform} fill="#000">
            <path d={BRIDGE} />
            <path d={EYE_L} />
            <path d={EYE_R} />
          </g>
        </mask>

        {/* The head with the band punched *out* of it, for monochrome. */}
        <mask id={cutId} maskUnits="userSpaceOnUse" x="0" y="0" width={BOX} height={BOX}>
          <rect width={BOX} height={BOX} fill="#000" />
          <path d={SQUIRCLE} fill="#fff" />
          <g transform={bandTransform}>
            <path d={BAND} fill="#000" />
          </g>
          <g transform={eyeTransform} fill="#fff">
            <path d={BRIDGE} />
            <path d={EYE_L} />
            <path d={EYE_R} />
          </g>
        </mask>
      </defs>

      {mono ? (
        /* Tinted head, white eyes.
           The head takes the belt colour, so the badge still reads as your rank
           at a glance. The mask band is cut out of it and falls through to
           transparent -- drawing it in the same colour would be invisible --
           and the eyes are painted white on top, matching the icon. */
        <>
          <rect width={BOX} height={BOX} fill={face} mask={`url(#${cutId})`} />
          <g transform={eyeTransform}>
            <path d={EYE_L} fill={eyeColor} />
            <path d={EYE_R} fill={eyeColor} />
          </g>
        </>
      ) : (
        <>
          <path
            d={SQUIRCLE}
            fill={face}
            stroke={outlineColor}
            strokeWidth={outlineColor ? 16 : undefined}
          />
          <g clipPath={`url(#${clipId})`}>
            <g transform={bandTransform}>
              <path d={BAND} fill={ring} />
            </g>
            {/* The bridge takes the head colour so the two eyes read as two,
                and the eyes are painted rather than cut, so they stay white on
                any surface instead of showing whatever is behind the mark. */}
            <g transform={eyeTransform}>
              <path d={BRIDGE} fill={face} />
              <path d={EYE_L} fill={eyeColor} />
              <path d={EYE_R} fill={eyeColor} />
            </g>
          </g>
        </>
      )}
    </svg>
  );
};

export default DojoMark;
